import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { appGeneratePath } from '@/shared/lib';

/** Шаблон адреса карточки диалога в разделе «Диалоги» */
export const CONVERSATION_ROUTE_PATH = '/dialogs/:id' as const;

/** Реплика, на которой открывается карточка диалога */
export const CONVERSATION_PHRASE_PARAM = 'phraseId';

/** Комментарий, на котором открывается карточка диалога */
export const CONVERSATION_COMMENT_PARAM = 'commentId';

/** Вкладка детализации, на которой открывается карточка диалога */
export const CONVERSATION_TAB_PARAM = 'tab';

/**
 * Условия открытия карточки диалога: на какой реплике, комментарии и вкладке. Имена задаются
 * константами модуля ({@link CONVERSATION_PHRASE_PARAM}, {@link CONVERSATION_COMMENT_PARAM}) —
 * их же читает карточка.
 */
export type ConversationLinkQuery = Record<string, string | undefined>;

/** Ссылка на карточку диалога: адрес и состояние возврата */
export type ConversationLink = {
  to: string;
  /** Адрес, куда карточка вернёт по «← Назад» */
  state: string;
};

/** Строка условий открытия; незаданные условия в адрес не идут */
export const buildConversationSearch = (query: ConversationLinkQuery): string =>
  new URLSearchParams(Object.entries(query).filter((entry): entry is [string, string] => entry[1] != null)).toString();

/**
 * Адрес карточки диалога в разделе «Диалоги» с условиями её открытия.
 *
 * Запись, открываемую из списка диалогов, адресует не эта функция, а `useDialogsSectionRoute`:
 * там запись остаётся в своём разделе (закреплённый пресет, выборки), и адрес зависит от него.
 */
export const buildConversationPath = (conversationId: number | string, query?: ConversationLinkQuery): string => {
  const path = appGeneratePath(CONVERSATION_ROUTE_PATH, { id: String(conversationId) });
  const search = query ? buildConversationSearch(query) : '';

  return search ? `${path}?${search}` : path;
};

/**
 * Полный адрес карточки диалога — вместе с адресом системы.
 *
 * Нужен там, где ссылку уносят за пределы приложения (буфер обмена, переписка, отчёт):
 * относительный путь вне приложения не откроется.
 */
export const buildConversationUrl = (conversationId: number | string, query?: ConversationLinkQuery): string =>
  `${window.location.origin}${buildConversationPath(conversationId, query)}`;

/**
 * Ссылка на карточку диалога с общим контрактом возврата: «← Назад» в карточке ведёт на тот
 * адрес, с которого в неё перешли, — вместе с его условиями (отбор, окно, порядок перечня),
 * поэтому возврат приводит к прежнему состоянию раздела, а не к его умолчанию.
 *
 * Отдаёт адрес и состояние перехода, а не готовую ссылку: у разделов, ведущих в диалог, разные
 * механики перехода (обычный `Link`, `<a>` со своим перехватом нажатия, императивный `navigate`),
 * общее у них — адрес карточки и то, куда она вернёт.
 */
export const useConversationLink = () => {
  const { pathname, search } = useLocation();

  return useCallback(
    (conversationId: number | string, query?: ConversationLinkQuery): ConversationLink => ({
      to: buildConversationPath(conversationId, query),
      state: `${pathname}${search}`,
    }),
    [pathname, search],
  );
};
